import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 18000 });
const mergeEntry = (previous, next) => previous?.scope === next?.scope
  && Number(previous?.lastAttemptAt || 0) > Number(next?.lastAttemptAt || 0) ? previous : next;

export default function useAreaPowerObservations(areaId, devices, enabled) {
  const membership = devices.map((device) => Number(device.id)).sort((a, b) => a - b).join(',');
  const key = `${areaId}:${membership}:${enabled}`;
  const [snapshot, setSnapshot] = useState({ key: '', byId: {}, pending: {}, error: '' });
  const scope = useRef(null);
  const refresh = useCallback(async (deviceId, manual = true) => {
    const current = scope.current;
    if (!current || current.key !== key) return;
    const entry = current.byId[deviceId];
    if (!entry?.adbEligible || !entry.schemaAvailable || current.pending.has(entry.targetDeviceId)) return;
    current.pending.add(entry.targetDeviceId);
    setSnapshot((value) => ({ ...value, pending: Object.fromEntries([...current.pending].map((id) => [id, true])) }));
    try {
      const response = await axios.post(`/api/site-control/areas/${areaId}/power-observations/${deviceId}`, { manual }, config());
      if (scope.current !== current || current.byId[deviceId]?.scope !== entry.scope) return;
      current.byId = { ...current.byId, [deviceId]: mergeEntry(current.byId[deviceId], response.data) };
      setSnapshot((value) => ({ ...value, byId: current.byId }));
    } catch {
      if (scope.current === current) setSnapshot((value) => ({ ...value, error: 'Power details unavailable' }));
    } finally {
      current.pending.delete(entry.targetDeviceId);
      if (scope.current === current) setSnapshot((value) => ({ ...value, pending: Object.fromEntries([...current.pending].map((id) => [id, true])) }));
    }
  }, [areaId, key]);

  useEffect(() => {
    const current = { key, byId: {}, pending: new Set(), loading: false };
    scope.current = current;
    setSnapshot({ key, byId: {}, pending: {}, error: '' });
    if (!enabled || !areaId || !membership) return () => { if (scope.current === current) scope.current = null; };
    const read = async () => {
      if (current.loading || document.visibilityState === 'hidden') return;
      current.loading = true;
      try {
        const response = await axios.get(`/api/site-control/areas/${areaId}/power-observations`, config());
        if (scope.current !== current || !Array.isArray(response.data?.devices)) return;
        const entries = response.data.devices.filter((entry) => membership.split(',').includes(String(entry.deviceId)));
        current.byId = Object.fromEntries(entries.map((entry) => [entry.deviceId, mergeEntry(current.byId[entry.deviceId], entry)]));
        setSnapshot((value) => ({ ...value, byId: current.byId, error: '' }));
        entries.forEach((entry) => {
          if (!entry.nativeFresh && entry.nextAttemptAt <= Date.now()) refresh(entry.deviceId, false);
        });
      } catch {
        if (scope.current === current) setSnapshot((value) => ({ ...value, error: 'Power details unavailable' }));
      } finally { current.loading = false; }
    };
    read();
    const timer = window.setInterval(read, 15000);
    document.addEventListener('visibilitychange', read);
    return () => {
      if (scope.current === current) scope.current = null;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', read);
    };
  }, [areaId, enabled, membership, key, refresh]);
  return { enabled, byId: snapshot.key === key ? snapshot.byId : {}, pending: snapshot.key === key ? snapshot.pending : {},
    error: snapshot.key === key ? snapshot.error : '', refresh };
}
